import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Filter parameters sent to the analytics summary endpoint.
 */
export interface AnalyticsFilter {
  businessUnitId?: string;
  from: string;
  to: string;
}

/**
 * A labelled scalar metric point — used for bar and donut charts.
 */
export interface MetricPoint {
  label: string;
  value: number;
}

/**
 * A single date-bucketed opt-in/out data point for the time-series line chart.
 */
export interface TimeSeriesPoint {
  date: string;
  optInCount: number;
  optOutCount: number;
}

/**
 * Aggregated analytics summary returned by POST /v1/preferenceanalytics/summary.
 */
export interface AnalyticsSummary {
  channelDistribution: MetricPoint[];
  optInOutTimeSeries: TimeSeriesPoint[];
  pageSubmissions: MetricPoint[];
  preferenceBreakdown: MetricPoint[];
}

/**
 * Angular service for the 4Preferences analytics API.
 * Uses HttpClient directly (not BaseApiService) because the endpoints are
 * non-standard (POST for summary, blob response for export).
 */
@Injectable()
export class PreferenceAnalyticsService {
  private readonly baseUrl: string;

  public constructor(private readonly httpClient: HttpClient) {
    this.baseUrl = environment.api.API_ENDPOINT;
  }

  /**
   * Fetches aggregated analytics summary for the four chart metrics.
   *
   * @param filter Date range and optional business unit filter.
   */
  public getSummary(filter: AnalyticsFilter): Observable<AnalyticsSummary> {
    return this.httpClient.post<AnalyticsSummary>(
      `${this.baseUrl}/preferenceanalytics/summary`,
      filter
    );
  }

  /**
   * Downloads analytics data as an Excel (.xlsx) blob.
   *
   * @param filter Date range and optional business unit filter.
   */
  public exportExcel(filter: AnalyticsFilter): Observable<Blob> {
    const params: Record<string, string> = {
      from: filter.from,
      to: filter.to
    };
    if (filter.businessUnitId) {
      params.businessUnit = filter.businessUnitId;
    }
    return this.httpClient.get(`${this.baseUrl}/preferenceanalytics/export`, {
      params,
      responseType: 'blob'
    });
  }
}
